import React, {useCallback, useEffect, useState} from "react";
import axios from 'axios';
import {useLocation, useNavigate} from "react-router-dom";
import {TRYON_SERVER_URL} from "../config";
import {useDispatch, useSelector} from 'react-redux';
import {setAccessToken, setRefreshToken, setUserData, setUserID} from "../store/AuthActions";
import {useAuthentication} from "../Authentication";
import {selectAccessToken, selectAuthenticated} from "../selectors";
import Spinner from "../components/Spinner";
import BrandMark from "../components/BrandMark";
import GoogleIcon from "../assets/GoogleIcon.svg";
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { clearAppStorage } from "../Utils/storage";
import { Eye, EyeOff } from "lucide-react";

export default function Auth() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const {isAuthenticated} = useAuthentication();
    const authenticated = useSelector(selectAuthenticated);
    const accessToken = useSelector(selectAccessToken);

    // Determine initial mode from URL or default to signin
    const getInitialMode = () => {
        const path = location.pathname;
        if (path === '/signup') return 'signup';
        return 'signin';
    };

    const [mode, setMode] = useState(getInitialMode()); // 'signin' or 'signup'
    
    // Form state
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    const [first_name, setFirstName] = useState("");
    const [last_name, setLastName] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [policyChecked, setPolicyChecked] = useState(false);
    
    // UI state
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showVerify, setShowVerify] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
    const [emailSent, setEmailSent] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState("");
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    
    // Google OAuth state
    const [user, setUser] = useState([]);
    const [profile, setProfile] = useState([]);

    // Update mode when URL changes
    useEffect(() => {
        const path = location.pathname;
        if (path === '/signup') {
            setMode('signup');
        } else if (path === '/signin' || path === '/auth') {
            setMode('signin');
        }
    }, [location.pathname]);

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // Input validation and sanitization helper functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) && email.length <= 254;
    };

    const validateUsername = (username) => {
        // Username: 3-30 chars, alphanumeric, underscore, hyphen, dot
        const usernameRegex = /^[a-zA-Z0-9._-]{3,30}$/;
        return usernameRegex.test(username);
    };

    const validateName = (name) => {
        // Name: 1-50 chars, letters, spaces, hyphens, apostrophes
        const nameRegex = /^[a-zA-Z\s'-]{1,50}$/;
        return nameRegex.test(name.trim());
    };

    const sanitizeInput = (input) => {
        // Remove potentially dangerous characters
        return input.trim().replace(/[<>]/g, '');
    };

    const fetchUserData = async (accessToken) => {
        try {
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/get/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                const userid = data?.id;
                dispatch(setUserID(userid));
                const dataWithoutPassword = {...data, password: undefined};
                dispatch(setUserData(JSON.stringify(dataWithoutPassword)));
            } else {
                console.error('Failed to fetch user data');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const registerUser = async (formData) => {
        try {
            const response = await axios.post(`${TRYON_SERVER_URL}/api/v1/user/create/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 201) {
                console.log('User registered successfully:', response.data);
            } else {
                setShowError(true);
                setErrorMessage("Unable to create an account.");
                throw new Error("Unable to create an account.");
            }
        } catch (error) {
            console.error('Error while registering user:', error?.response?.data?.message);
            setShowError(true);
            setErrorMessage(error?.response?.data?.message || "Unable to create an account.");
            throw error;
        }
    };

    const loginUser = async (formData) => {
        try {
            const response = await axios.post(`${TRYON_SERVER_URL}/api/v1/user/token/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const {access, refresh} = response.data;
            clearAppStorage();
            dispatch(setAccessToken(access));
            dispatch(setRefreshToken(refresh));
            await isAuthenticated();
            await fetchUserData(access);
            
            // Navigate based on location state or default
            if (location.state && location.state.path) {
                navigate(location.state.path);
            } else {
                navigate("/");
            }
            setSpinner(false);
        } catch (error) {
            // Set showVerify for email/password login if account is not active
            if(error?.response?.data?.is_active === false){
                setShowVerify(true);
            }
            throw error;
        }
    };

    const googleSignIn = async (googleAccessToken, profileData) => {
        try {
            const formData = new FormData();
            formData.append('access_token', googleAccessToken);
            
            // Generate username for new user registration if profile data is available
            if (profileData) {
                const baseUsername = profileData.given_name?.replace(/[^a-zA-Z0-9@/./+/-/_]/g, '') || 'user';
                const uniqueUsername = `${baseUsername}${Date.now()}`;
                formData.append('username', uniqueUsername);
            }

            const response = await axios.post(`${TRYON_SERVER_URL}/api/v1/user/google/signin/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const {access, refresh} = response.data;
            clearAppStorage();
            dispatch(setAccessToken(access));
            dispatch(setRefreshToken(refresh));
            await isAuthenticated();
            await fetchUserData(access);
            
            // Navigate based on location state or default
            if (location.state && location.state.path) {
                navigate(location.state.path);
            } else {
                navigate("/");
            }
            setSpinner(false);
        } catch (error) {
            console.error('Error with Google signin:', error?.response?.data);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage("");
        setShowError(false);
        setSpinner(true);

        if (mode === 'signin') {
            // Sign in validation
            if (email === "" || password === "") {
                setErrorMessage("Enter valid email and password");
                setShowError(true);
                setSpinner(false);
                return;
            }

            // Validate email format
            const sanitizedEmail = sanitizeInput(email);
            if (!validateEmail(sanitizedEmail)) {
                setErrorMessage("Please enter a valid email address");
                setShowError(true);
                setSpinner(false);
                return;
            }

            try {
                const formData = new FormData();
                formData.append('email', sanitizedEmail);
                formData.append('password', password);
                await loginUser(formData);
            } catch (error) {
                setSpinner(false);
                setErrorMessage(error?.response?.data?.message || "Failed to sign in. Please try again.");
                setShowError(true);
            }
        } else {
            // Sign up validation
            if (username === "" || first_name === "" || last_name === "" || email === "" || password === "") {
                setErrorMessage("Please enter all credentials");
                setShowError(true);
                setSpinner(false);
                return;
            }

            // Validate and sanitize inputs
            const sanitizedUsername = sanitizeInput(username);
            const sanitizedFirstName = sanitizeInput(first_name);
            const sanitizedLastName = sanitizeInput(last_name);
            const sanitizedEmail = sanitizeInput(email);

            if (!validateUsername(sanitizedUsername)) {
                setErrorMessage("Username must be 3-30 characters and contain only letters, numbers, dots, hyphens, or underscores");
                setShowError(true);
                setSpinner(false);
                return;
            }

            if (!validateName(sanitizedFirstName) || !validateName(sanitizedLastName)) {
                setErrorMessage("Name must be 1-50 characters and contain only letters, spaces, hyphens, or apostrophes");
                setShowError(true);
                setSpinner(false);
                return;
            }

            if (!validateEmail(sanitizedEmail)) {
                setErrorMessage("Please enter a valid email address");
                setShowError(true);
                setSpinner(false);
                return;
            }

            if (password.length < 8) {
                setErrorMessage("Password must be at least 8 characters long");
                setShowError(true);
                setSpinner(false);
                return;
            }

            if (!policyChecked) {
                setErrorMessage("Accept our privacy policy and terms of use");
                setShowError(true);
                setSpinner(false);
                return;
            }

            try {
                const formData = new FormData();
                formData.append('username', sanitizedUsername);
                formData.append('first_name', sanitizedFirstName);
                formData.append('last_name', sanitizedLastName);
                formData.append('email', sanitizedEmail);
                formData.append('password', password);

                await registerUser(formData);
                setSpinner(false);
                // Remove password from navigation state for security
                navigate("/verify", {
                    state: {
                        username: sanitizedUsername,
                        first_name: sanitizedFirstName,
                        last_name: sanitizedLastName,
                        email: sanitizedEmail,
                        // Password removed - backend should handle verification via OTP
                    },
                });
            } catch (error) {
                setSpinner(false);
                setErrorMessage(error?.response?.data?.message || "Failed to sign up. Please try again.");
                setShowError(true);
            }
        }
    };

    const regenerateOtp = async (email) => {
        try {
            const response = await fetch(`${TRYON_SERVER_URL}/api/v1/user/otp/regenerate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email }),
            });

            if (!response.ok) {
                throw new Error("Failed to regenerate OTP. Please try again.");
            }
            setErrorMessage("");
        } catch (error) {
            setShowError(true);
            setErrorMessage(error.message);
        }
    };

    const requestPasswordReset = async (email) => {
        try {
            setForgotPasswordLoading(true);
            setForgotPasswordError("");

            const response = await axios.post(
                `${TRYON_SERVER_URL}/api/v1/user/password/reset/request/`,
                { email: email },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 200 || response.status === 201) {
                setEmailSent(true);
                setForgotPasswordError("");
            } else {
                throw new Error("Failed to send password reset link. Please try again.");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                "Failed to send reset link. Please try again.";
            setForgotPasswordError(errorMessage);
            setEmailSent(false);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const navigateVerify = async () => {
        if (profile && Object.keys(profile).length > 0) {
            await regenerateOtp(profile.email);
            navigate("/verify", {
                state: {
                    email: profile.email,
                    // Password removed - backend should handle verification via OTP
                },
            });
        } else {
            const sanitizedEmail = sanitizeInput(email);
            await regenerateOtp(sanitizedEmail);
            navigate("/verify", {
                state: {
                    email: sanitizedEmail,
                    // Password removed - backend should handle verification via OTP
                },
            });
        }
    };

    const checkAuthentication = useCallback(async () => {
        try {
            const userAuthenticated = await isAuthenticated();
            if (userAuthenticated) {
                if(location.state !== null && location.state.path) {
                    navigate(location.state.path);
                } else {
                    navigate("/");
                }
            }
        } catch (error) {
            console.error('Error checking authentication:', error);
            if (error.message && error.message.toLowerCase().includes('network')) {
                console.error('Network error, redirecting to login page');
            }
        }
    }, [isAuthenticated, navigate, location.state]);

    useEffect(() => {
        if (!authenticated) {
            checkAuthentication();
        }
    }, [authenticated, checkAuthentication]);

    // Google OAuth
    const login = useGoogleLogin({
        onSuccess: (codeResponse) => {setUser(codeResponse); console.log("codeResponse", codeResponse)},
        onError: (error) => {console.log('Google Login Failed:', error); setShowError(true); setErrorMessage("Failed to sign in with Google. Please try again."); setSpinner(false);}
    });

    useEffect(() => {
        if (user && user.access_token) {
            // Use Authorization header instead of URL parameter for security
            axios
                .get(`https://www.googleapis.com/oauth2/v1/userinfo`, {
                    headers: {
                        Authorization: `Bearer ${user.access_token}`,
                        Accept: 'application/json'
                    }
                })
                .then((res) => {
                    setProfile(res.data);
                })
                .catch((err) => console.log(err));
        }
    }, [user]);

    // Handle Google OAuth: send access_token to backend for verification and account creation
    useEffect(() => {
        const handleGoogleAuth = async () => {
            // Only proceed if both profile and user have actual data (not empty arrays)
            if (profile && user && Object.keys(profile).length > 0 && Object.keys(user).length > 0) {
                try {
                    const accessToken = user.access_token;
                    
                    if (!accessToken) {
                        throw new Error("Google access token not available");
                    }
                    
                    // Send the access_token to backend
                    // Backend will verify it with Google and create/signin the account
                    await googleSignIn(accessToken, profile);
                } catch (error) {
                    console.error('Error with Google authentication:', error?.response?.data);
                    setShowError(true);
                    setErrorMessage(error?.response?.data?.message || error?.response?.data?.error || error?.message || "Failed to sign in with Google. Please try again.");
                }
            }
        };

        handleGoogleAuth();
    }, [profile, user]);

    const switchMode = (newMode) => {
        setMode(newMode);
        setShowError(false);
        setErrorMessage("");
        // Clear form when switching
        if (newMode === 'signin') {
            setUsername("");
            setFirstName("");
            setLastName("");
            setPolicyChecked(false);
        }
        // Update URL
        navigate(newMode === 'signup' ? '/signup' : '/signin', { replace: true });
    };

    return (
        <div className="flex h-screen flex-col overflow-y-auto bg-ground">
            <div className="flex min-h-full w-full items-start justify-center px-4 py-8">
                {!authenticated ? (
                    <div className="mx-auto flex w-full max-w-lg justify-center">
                        <div className="w-full rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                            <div className="flex items-center justify-center gap-3 px-6 pb-2 pt-8">
                                <BrandMark alt="" className="h-9 w-9 shrink-0 object-contain"/>
                                <div className="font-Mulish text-lg font-semibold text-secondary">
                                    TryOn AI
                                </div>
                            </div>

                            <div className="flex border-b border-hairline">
                                <button
                                    onClick={() => switchMode('signin')}
                                    className={`flex-1 px-4 py-3 text-center font-Mulish text-sm ${
                                        mode === 'signin'
                                            ? 'border-b-2 border-primary-500 font-semibold text-secondary'
                                            : 'text-graphite'
                                    }`}
                                >
                                    Sign in
                                </button>
                                <button
                                    onClick={() => switchMode('signup')}
                                    className={`flex-1 px-4 py-3 text-center font-Mulish text-sm ${
                                        mode === 'signup'
                                            ? 'border-b-2 border-primary-500 font-semibold text-secondary'
                                            : 'text-graphite'
                                    }`}
                                >
                                    Sign up
                                </button>
                            </div>

                            <div className="p-6">
                                <button
                                    type="button"
                                    onClick={login}
                                    className="flex w-full items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                                >
                                    <img src={GoogleIcon} alt="" className="h-4 w-4"/>
                                    Continue with Google
                                </button>
                                <p className="mt-2 text-center font-Mulish text-xs text-graphite">
                                    We'll sign you in, or create an account.
                                </p>

                                <div className="my-4 flex items-center">
                                    <div className="h-px flex-grow bg-hairline"></div>
                                    <span className="mx-3 whitespace-nowrap font-Mulish text-sm text-graphite">
                                        or use email
                                    </span>
                                    <div className="h-px flex-grow bg-hairline"></div>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    {/* Username - Only for signup */}
                                    {mode === 'signup' && (
                                        <div className="mb-3 sm:mb-4">
                                            <label htmlFor="username" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                                Username
                                            </label>
                                            <input 
                                                type="text" 
                                                name="username" 
                                                id="username" 
                                                placeholder="eg. tryonlabs"
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                className="mt-1 w-full rounded-lg border-0 bg-white px-3 py-2.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                required
                                            />
                                        </div>
                                    )}

                                    {/* First Name & Last Name - Only for signup */}
                                    {mode === 'signup' && (
                                        <div className="mb-3 sm:mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                            <div>
                                                <label htmlFor="firstName" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                                    First Name
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="firstName" 
                                                    id="firstName"
                                                    placeholder="eg. Tryon"
                                                    value={first_name}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    className="mt-1 w-full rounded-lg border-0 bg-white px-3 py-2.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="lastName" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                                    Last Name
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="lastName" 
                                                    id="lastName"
                                                    placeholder="eg. Labs"
                                                    value={last_name}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    className="mt-1 w-full rounded-lg border-0 bg-white px-3 py-2.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Email */}
                                    <div className="mb-3 sm:mb-4">
                                        <label htmlFor="email" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                            Email
                                        </label>
                                        <input 
                                            type="email" 
                                            name="email" 
                                            id="email"
                                            placeholder="eg. tryon@tryonlabs.ai"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="mt-1 w-full rounded-lg border-0 bg-white px-3 py-2.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            required
                                        />
                                    </div>

                                    {/* Password */}
                                    <div className="my-3 sm:my-4">
                                        <label htmlFor="password" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                            Password
                                        </label>
                                        <div className="relative flex justify-between items-center">
                                            <input 
                                                type={passwordVisible ? "text" : "password"} 
                                                name="password" 
                                                id="password"
                                                placeholder="*********"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="mt-1 w-full rounded-lg border-0 bg-white px-3 py-2.5 pr-10 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                required
                                            />
                                            {passwordVisible ? <Eye onClick={togglePasswordVisibility} aria-label="Hide password" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-gray-400" /> : <EyeOff onClick={togglePasswordVisibility} aria-label="Show password" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-gray-400" />}
                                        </div>
                                    </div>

                                    {/* Remember Me - Only for signin */}
                                    {mode === 'signin' && (
                                        <div className="mt-4 flex items-center">
                                            <input
                                                id="remember"
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-primary-200 text-primary-700 focus:ring-primary-200"
                                            />
                                            <label htmlFor="remember" className="ms-2 font-Mulish text-sm text-graphite">
                                                Remember me
                                            </label>
                                        </div>
                                    )}

                                    {/* Privacy Policy Checkbox - Only for signup */}
                                    {mode === 'signup' && (
                                        <div className="flex justify-start items-start gap-2 sm:gap-3 my-3 sm:my-4">
                                            <input 
                                                type="checkbox" 
                                                checked={policyChecked} 
                                                onChange={(e) => setPolicyChecked(e.target.checked)}
                                                className="mt-0.5 flex-shrink-0"
                                                required
                                            />
                                            <div className="flex h-fit">
                                                <span className="font-Mulish text-sm leading-5 text-graphite">
                                                    I agree to the&nbsp;
                                                    <a
                                                        href="/privacy-policy"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary-700 hover:underline"
                                                    >
                                                        privacy policy
                                                    </a>.
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        className={`mt-4 inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 font-Mulish text-sm font-semibold ${
                                            spinner
                                                ? "bg-primary-100 text-primary-800"
                                                : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                                        }`}
                                    >
                                        {spinner ? (
                                            <Spinner />
                                        ) : (
                                            mode === 'signin' ? 'Sign in' : 'Sign up'
                                        )}
                                    </button>

                                    {mode === 'signin' && (
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotPasswordModal(true)}
                                            className="mt-4 w-full text-center font-Mulish text-sm text-primary-700 hover:underline"
                                        >
                                            Forgot password
                                        </button>
                                    )}

                                    <div className="mt-4 text-center">
                                        <span className="font-Mulish text-sm text-graphite">
                                            {mode === 'signin' ? "Don't have an account? " : "Already have an account? "}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}
                                            className="font-Mulish text-sm text-primary-700 hover:underline"
                                        >
                                            {mode === 'signin' ? 'Sign up' : 'Sign in'}
                                        </button>
                                    </div>

                                    {showError && (
                                        <p className="mt-4 border-l-2 border-primary-700 pl-3 font-Mulish text-sm text-secondary">
                                            {errorMessage}
                                            {showVerify && (
                                                <>
                                                    {" "}
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            navigateVerify();
                                                        }}
                                                        className="text-primary-700 hover:underline"
                                                    >
                                                        Enter the code
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex h-screen items-center justify-center">
                        <Spinner/>
                    </div>
                )}
            </div>

            {/* Forgot Password Modal */}
            {showForgotPasswordModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
                    onClick={() => {
                        setShowForgotPasswordModal(false);
                        setForgotPasswordEmail("");
                        setForgotPasswordError("");
                    }}
                >
                    <div
                        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!emailSent ? (
                            <>
                                <div className="mb-4 flex items-center justify-between">
                                    <h2 className="font-Mulish text-lg font-semibold text-secondary">Reset password</h2>
                                    <button
                                        type="button"
                                        aria-label="Close"
                                        onClick={() => {
                                            setShowForgotPasswordModal(false);
                                            setForgotPasswordEmail("");
                                            setForgotPasswordError("");
                                        }}
                                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white font-Mulish text-sm text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
                                    >
                                        ×
                                    </button>
                                </div>

                                <p className="font-Mulish text-sm text-graphite">
                                    We'll send a reset link to this email.
                                </p>

                                <div className="mt-4">
                                    <label htmlFor="forgot-email" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="forgot-email"
                                        placeholder="you@example.com"
                                        value={forgotPasswordEmail}
                                        onChange={(e) => {
                                            setForgotPasswordEmail(e.target.value);
                                            setForgotPasswordError("");
                                        }}
                                        className="mt-1 w-full rounded-lg border-0 bg-white px-3 py-2.5 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        required
                                    />
                                    {forgotPasswordError && (
                                        <p className="mt-3 border-l-2 border-primary-700 pl-3 font-Mulish text-sm text-secondary">{forgotPasswordError}</p>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!forgotPasswordEmail) {
                                            setForgotPasswordError("Enter your email.");
                                            return;
                                        }

                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                        if (!emailRegex.test(forgotPasswordEmail)) {
                                            setForgotPasswordError("Enter a valid email.");
                                            return;
                                        }

                                        await requestPasswordReset(forgotPasswordEmail);
                                    }}
                                    disabled={forgotPasswordLoading}
                                    className={`mt-4 inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 font-Mulish text-sm font-semibold ${
                                        forgotPasswordLoading
                                            ? "bg-primary-100 text-primary-800"
                                            : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                                    }`}
                                >
                                    {forgotPasswordLoading ? <Spinner /> : "Send reset link"}
                                </button>
                            </>
                        ) : (
                            <div className="rounded-2xl bg-primary-50 p-6 text-center">
                                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h2 className="font-Mulish text-lg font-semibold text-secondary">Check your email</h2>
                                <p className="mt-1 font-Mulish text-sm text-graphite">
                                    A reset link is on its way to {forgotPasswordEmail}.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForgotPasswordModal(false);
                                        setForgotPasswordEmail("");
                                        setEmailSent(false);
                                        setForgotPasswordError("");
                                    }}
                                    className="mt-4 w-full rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

